import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import type { ReactNode } from 'react'

// Utility Methods
// ------------------------------------------------------------------------- /
function createWrapperAndAppendToBody(wrapperId: string): HTMLDivElement {
  const wrapperElement = document.createElement('div')
  wrapperElement.setAttribute('id', wrapperId)
  document.body.append(wrapperElement)

  return wrapperElement
}

type PortalProps = {
  children: ReactNode
  wrapperId: string
}

const Portal = ({ wrapperId, children }: PortalProps) => {
  const [wrapperElement, setWrapperElement] = useState<Element>()

  useLayoutEffect(() => {
    let element = document.querySelector(`#${wrapperId}`)
    // if element is not found with wrapperId or wrapperId is not provided,
    // create and append to body
    if (!element) {
      element = createWrapperAndAppendToBody(wrapperId)
    }
    setWrapperElement(element)

    // The wrapper is intentionally left in the DOM on unmount. wrapperId is a
    // stable, shared id (multiple Modals/BottomSheets reuse the same one), so at
    // most one wrapper ever exists per id. Removing it on unmount would tear the
    // portal target out from under other portals still using it — and under
    // StrictMode's mount/unmount/remount cycle it can leave the target detached,
    // so portalled content (e.g. a modal) never appears in the page.
  }, [wrapperId])

  // wrapperElement state will be null on the very first render.
  if (wrapperElement === undefined) {
    return null
  }

  return createPortal(children, wrapperElement)
}

export { Portal }
